import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { StatWidget } from '@/components/widgets/StatWidget';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { formatDateTime, formatNumber } from '@/utils/format';
import { feedMonitoringApi } from '@/api/services/feedMonitoring';
import type { FeedMonitoringAlertDto, FeedMonitoringAlertsFilterInput } from '@/api/types/feedMonitoring';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function dateStr(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function todayStart() {
  return `${dateStr(new Date())}T00:00:00`;
}
function todayEnd() {
  return `${dateStr(new Date())}T23:59:59`;
}

interface Range {
  from: string;
  to: string;
}

const highestLagLabel = (symbol: string, lag: number) =>
  `${symbol}${lag > 0 ? ` (${lag.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })})` : ''}`;

export function NotificationsTab() {
  const [draft, setDraft] = useState<Range>({ from: todayStart(), to: todayEnd() });
  const [applied, setApplied] = useState<Range>(draft);

  const appliedInput = useMemo<FeedMonitoringAlertsFilterInput>(() => ({ from: applied.from, to: applied.to }), [applied]);
  const rangeValid = Boolean(applied.from && applied.to);

  const table = useServerDataTable<FeedMonitoringAlertDto, FeedMonitoringAlertsFilterInput>({
    queryKey: ['feed-monitoring', 'alerts'],
    fetcher: (req) => feedMonitoringApi.getAlertsPaged(req),
    pageSize: 5,
    extraParams: appliedInput,
    initialSort: { key: 'triggeredAt', dir: 'desc' },
    enabled: rangeValid,
  });

  const summary = useQuery({
    queryKey: ['feed-monitoring', 'alerts-summary', appliedInput],
    queryFn: () => feedMonitoringApi.getAlertsSummary(appliedInput),
    enabled: rangeValid,
  });

  // Keep the grid search bound to the Fetch-applied range (mirrors filtersForm wiring).
  const fetch = () => {
    if (!draft.from || !draft.to) return;
    table.setPageIndex(0);
    setApplied({ ...draft });
  };

  // When the applied range changes, re-fetch the summary (react-query key handles it).
  useEffect(() => {
    if (rangeValid) void summary.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedInput]);

  const s = summary.data;

  const fields = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Time Range</label>
          <DateRangePicker
            mode="range"
            enableTime
            timeSeconds
            maxDate={todayEnd()}
            value={{ start: draft.from, end: draft.to }}
            onChange={(v) => setDraft({ from: v.start, to: v.end })}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
  ];

  const columns: DataGridColumn<FeedMonitoringAlertDto>[] = [
    { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
    { key: 'source', title: 'Source', className: 'text-start', sortable: false, value: (r) => r.source },
    { key: 'lagSeconds', title: 'Lag (s)', className: 'text-end', sortable: false, value: (r) => r.lagSeconds, cell: (r) => formatNumber(r.lagSeconds, 1) },
    { key: 'allowedLagSeconds', title: 'Allowed Lag (s)', className: 'text-end', value: (r) => r.allowedLagSeconds, cell: (r) => formatNumber(r.allowedLagSeconds, 1) },
    { key: 'triggeredAt', title: 'Triggered At', className: 'text-start', value: (r) => r.triggeredAt, cell: (r) => formatDateTime(r.triggeredAt) },
  ];

  return (
    <>
      <FilterBar
        card={false}
        fields={fields}
        actions={
          <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching || summary.isFetching} onClick={fetch}>
            Fetch
          </AsyncButton>
        }
      />

      <div className="row row-cols-1 row-cols-md-3 g-2 my-3">
        <div className="col">
          <StatWidget label="Total Alerts" value={s?.totalAlerts ?? 0} icon="fa fa-bell" theme="primary" />
        </div>
        <div className="col">
          <StatWidget label="Unique Symbols" value={s?.uniqueSymbols ?? 0} icon="fa fa-list" theme="info" />
        </div>
        <div className="col">
          <StatWidget label="Highest Lag Symbol" value={s ? highestLagLabel(s.highestLagSymbol, s.highestLag) : '-'} icon="fa fa-exclamation-triangle" theme="danger" />
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        includeExcel
        excelFileName="Feed Monitoring Alerts"
        emptyMessage={table.isLoading ? 'Loading…' : 'No alerts in this range.'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />
    </>
  );
}
